// ============================================
// User Types
// ============================================

export type UserRole = 'user' | 'tester' | 'admin';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatar?: string;
  isPremium?: boolean;
  appleSub?: string;
  googleSub?: string;
  freeCoins?: number;
  paidCoins?: number;
  lastFreeRefillDate?: string;
  otaOptInBeta?: boolean;
  createdAt: Date;
}

// ============================================
// Push Notifications
// ============================================

export type DevicePlatform = 'ios' | 'android';

export interface DeviceRegistration {
  token: string;
  platform: DevicePlatform;
  deviceId: string;
  enabled: boolean;
}

export type PushNotificationType =
  | 'story_turn'
  | 'story_join'
  | 'story_done'
  | 'adventure_turn'
  | 'adventure_join'
  | 'adventure_started'
  | 'adventure_done';

export interface PushNotificationData {
  type: PushNotificationType;
  storyId?: string;
  adventureId?: string;
  comicShareCode?: string;
}

// ============================================
// Coins / Wallet
// ============================================

export interface CoinBalance {
  free: number;
  paid: number;
  total: number;
  dailyFree: number;
  /** ISO timestamp of next UTC midnight */
  nextRefillAt: string;
}

export type CoinTransactionType = 'free_refill' | 'purchase' | 'debit' | 'admin_credit' | 'refund';
export type CoinChargeReason =
  | 'create_story'
  // Legacy symbol — used to be charged when an adventure was created. Now the
  // create endpoint is free; cost moved to per-premise generation. Kept in the
  // union so historical CoinTransaction documents still type-check.
  | 'create_adventure'
  | 'adventure_premise'
  | 'adventure_portrait'
  | 'adventure_action'
  | 'regenerate_cover'
  | 'regenerate_story'
  | 'regenerate_comic';

export interface CoinCosts {
  create_story: number;
  /** @deprecated Cost is now 0; charge moved to adventure_premise per generation. */
  create_adventure: number;
  adventure_premise: number;
  /** Per-portrait cost charged at /start, once per participant with a character. */
  adventure_portrait: number;
  adventure_action: number;
  regenerate_cover: number;
  regenerate_story: number;
  /** Re-run the post-adventure premium narrative rewrite over the same images. */
  regenerate_comic: number;
}

export interface UserPublic {
  id: string;
  name: string;
  avatar?: string;
}

// ============================================
// Story Types
// ============================================

export type StoryGenre = 'comedy' | 'horror' | 'romance' | 'scifi' | 'fantasy' | 'random';
export type StoryVisibility = 'public' | 'unlisted';

export type StoryStatus = 'active' | 'completed' | 'expired';

export type AIProvider = 'xai' | 'openai' | 'anthropic';

export const AI_PROVIDERS: { value: AIProvider; label: string }[] = [
  { value: 'xai', label: 'Grok (xAI)' },
  { value: 'openai', label: 'GPT (OpenAI)' },
  { value: 'anthropic', label: 'Claude (Anthropic)' },
];

// Pricing is in millicents (1 cent = 1000 millicents).
// Text pricing is per 1K tokens. Image pricing is per image, keyed by "size_quality".
export interface TextModelPricing {
  input: number;
  output: number;
}

export type ImageModelPricing = Record<string, number>;

export interface AIModelConfig {
  value: string;
  label: string;
  pricing: TextModelPricing;
}

export interface ImageModelConfig {
  value: string;
  label: string;
  pricing: ImageModelPricing;
}

export const AI_MODELS: Record<AIProvider, AIModelConfig[]> = {
  xai: [
    {
      value: 'grok-4-1-fast-non-reasoning',
      label: 'Grok 4.1',
      pricing: { input: 200, output: 500 }, // $0.20/$0.50 per 1M
    },
    {
      value: 'grok-4.20-0309-non-reasoning',
      label: 'Grok 4.2',
      pricing: { input: 200, output: 500 }, // TODO verify
    },
  ],
  openai: [
    {
      value: 'gpt-5.4-nano',
      label: 'GPT-5.4 nano',
      pricing: { input: 50, output: 400 }, // $0.05/$0.40 per 1M — TODO verify
    },
    {
      value: 'gpt-5.4-mini',
      label: 'GPT-5.4 mini',
      pricing: { input: 250, output: 2000 }, // $0.25/$2.00 per 1M — TODO verify
    },
    {
      value: 'gpt-5.4',
      label: 'GPT-5.4',
      pricing: { input: 1250, output: 10000 }, // $1.25/$10.00 per 1M — TODO verify
    },
  ],
  anthropic: [
    {
      value: 'claude-sonnet-4-6',
      label: 'Claude Sonnet 4.6',
      pricing: { input: 3000, output: 15000 }, // $3.00/$15.00 per 1M
    },
    {
      value: 'claude-opus-4-7',
      label: 'Claude Opus 4.7',
      pricing: { input: 15000, output: 75000 }, // $15.00/$75.00 per 1M
    },
    {
      value: 'claude-haiku-4-5',
      label: 'Claude Haiku 4.5',
      pricing: { input: 1000, output: 5000 }, // $1.00/$5.00 per 1M
    },
  ],
};

// Cheap/fast models for simple tasks (rewriting, titles, summaries)
export const CHEAP_AI_MODELS: Record<AIProvider, string> = {
  xai: 'grok-4-1-fast-non-reasoning',
  openai: 'gpt-5.4-nano',
  anthropic: 'claude-haiku-4-5',
};

// Premium/expensive models for the few high-value tasks where quality
// dominates cost — currently: post-adventure comic narrative rewrite,
// where the AI is allowed to take game transcripts as inspiration and
// write a coherent, polished story.
export const PREMIUM_AI_MODELS: Record<AIProvider, string> = {
  xai: 'grok-4.20-0309-non-reasoning',
  openai: 'gpt-5.4',
  anthropic: 'claude-opus-4-7',
};

// Image generation providers
export type ImageProvider = 'openai' | 'fal' | 'xai';

export const IMAGE_PROVIDERS: { value: ImageProvider; label: string }[] = [
  { value: 'fal', label: 'FLUX (FAL)' },
  { value: 'openai', label: 'GPT Image (OpenAI)' },
  { value: 'xai', label: 'Grok Imagine (xAI)' },
];

export const IMAGE_MODELS: Record<ImageProvider, ImageModelConfig[]> = {
  fal: [
    {
      value: 'fal-flux-dev',
      label: 'FLUX Dev',
      pricing: { '1024x1024_standard': 2500, '1024x1024_low': 2500 }, // $0.025 — TODO verify
    },
    {
      value: 'fal-flux-schnell',
      label: 'FLUX Schnell',
      pricing: { '1024x1024_standard': 300, '1024x1024_low': 300 }, // $0.003 — TODO verify
    },
  ],
  openai: [
    {
      value: 'gpt-image-1.5',
      label: 'GPT Image 1.5',
      pricing: {
        '1024x1024_low': 1100,    // $0.011
        '1024x1024_medium': 2200, // $0.022
        '1024x1024_high': 4400,   // $0.044
      },
    },
    {
      value: 'gpt-image-1-mini',
      label: 'GPT Image 1 Mini',
      pricing: {
        '1024x1024_low': 500,   // $0.005 — TODO verify
        '1024x1024_medium': 1000,
        '1024x1024_high': 2000,
      },
    },
  ],
  xai: [
    {
      value: 'grok-imagine-image',
      label: 'Grok Imagine',
      pricing: {
        '1024x1024_standard': 20000, // $0.20 — confirmed from API response
        '1024x1024_low': 20000,
      },
    },
    {
      value: 'grok-imagine-image-pro',
      label: 'Grok Imagine Pro',
      pricing: {
        '1024x1024_standard': 40000, // $0.40 — TODO verify
        '1024x1024_low': 40000,
      },
    },
  ],
};

export const DEFAULT_IMAGE_MODEL: Record<ImageProvider, string> = {
  fal: 'fal-flux-dev',
  openai: 'gpt-image-1.5',
  xai: 'grok-imagine-image',
};

// Pricing for legacy/retired models that may still appear in historical usage records
// but are no longer offered in AI_MODELS / IMAGE_MODELS.
export const LEGACY_TEXT_PRICING: Record<string, TextModelPricing> = {
  'gpt-4o': { input: 2500, output: 10000 },
  'gpt-4o-mini': { input: 150, output: 600 },
  'gpt-4-turbo': { input: 10000, output: 30000 },
  'gpt-5.2-2025-12-11': { input: 1250, output: 10000 },
  'claude-3-5-sonnet-20241022': { input: 3000, output: 15000 },
  'claude-3-haiku-20240307': { input: 250, output: 1250 },
  'claude-haiku-4-5-20251001': { input: 1000, output: 5000 },
  'claude-sonnet-4-5-20250929': { input: 3000, output: 15000 },
  'claude-opus-4-5-20251101': { input: 15000, output: 75000 },
  'grok-3-fast': { input: 5000, output: 15000 },
  'grok-3-mini-fast': { input: 300, output: 500 },
};

export const LEGACY_IMAGE_PRICING: Record<string, ImageModelPricing> = {
  'gpt-image-1': {
    '1024x1024_low': 4000,    // $0.04
    '1024x1024_medium': 8000, // $0.08
    '1024x1024_high': 16000,  // $0.16
  },
  'fal-flux-2-edit': {
    '1024x1024_standard': 6000,
    '1024x1024_low': 6000,
  },
};

// Flattened lookup maps (AI_MODELS + IMAGE_MODELS + legacy) for usage-cost calculation.
export const TEXT_PRICING: Record<string, TextModelPricing> = {
  ...LEGACY_TEXT_PRICING,
  ...Object.fromEntries(
    (Object.values(AI_MODELS).flat() as AIModelConfig[]).map((m) => [m.value, m.pricing])
  ),
};

export const IMAGE_PRICING: Record<string, ImageModelPricing> = {
  ...LEGACY_IMAGE_PRICING,
  ...Object.fromEntries(
    (Object.values(IMAGE_MODELS).flat() as ImageModelConfig[]).map((m) => [m.value, m.pricing])
  ),
};

// Fallback when an unknown model is recorded (preserves logging without crashing).
export const DEFAULT_TEXT_PRICING: TextModelPricing = { input: 500, output: 1500 };
export const DEFAULT_IMAGE_PRICING: ImageModelPricing = {
  '1024x1024_low': 1100,
  '1024x1024_medium': 2200,
  '1024x1024_high': 4400,
  '1024x1024_standard': 1100,
};

export type ParticipantStatus = 'pending' | 'writing' | 'done';

export interface Participant {
  userId?: string;
  anonymousId?: string;
  displayName?: string;
  user?: UserPublic;
  order: number;
  status: ParticipantStatus;
  joinedAt: Date;
}

export interface Story {
  id: string;
  code: string;
  creatorId: string;
  title?: string;
  subhead?: string;
  genre: StoryGenre;
  maxParticipants: number;
  status: StoryStatus;
  participants: Participant[];
  currentTurn: number;
  expiresAt: Date;
  finalStory?: string;
  coverImage?: string;
  coverImageMedium?: string;
  coverImageThumb?: string;
  creatorHint?: string;
  showHintToParticipants?: boolean;
  aiProvider?: AIProvider;
  aiModel?: string;
  reactions?: Partial<Record<string, number>>;
  reactionCount?: number;
  language?: string;
  visibility?: StoryVisibility;
  createdAt: Date;
}

export interface StoryListItem {
  id: string;
  code: string;
  genre: StoryGenre;
  status: StoryStatus;
  currentTurn: number;
  maxParticipants: number;
  participantsCount: number;
  isMyTurn: boolean;
  title?: string;
  subhead?: string;
  coverImage?: string;
  coverImageMedium?: string;
  coverImageThumb?: string;
  reactions?: Partial<Record<string, number>>;
  reactionCount?: number;
  language?: string;
  visibility?: StoryVisibility;
  createdAt: Date;
}

// ============================================
// Entry Types
// ============================================

export interface Entry {
  id: string;
  storyId: string;
  userId?: string;
  anonymousId?: string;
  displayName?: string;
  user?: UserPublic;
  order: number;
  text: string;
  createdAt: Date;
}

// ============================================
// API Request/Response Types
// ============================================

export interface AuthRequestCodeRequest {
  email: string;
}

export interface AuthVerifyRequest {
  email: string;
  code: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface CreateStoryRequest {
  genre: StoryGenre;
  maxParticipants: number;
  firstEntry: string;
}

export interface CreateStoryResponse {
  story: Story;
  shareUrl: string;
}

export interface JoinStoryRequest {
  name?: string;
}

export interface AddEntryRequest {
  text: string;
}

export interface StoryDetailResponse {
  story: Story;
  entries: Entry[];
  previousEntry?: Entry;
  canWrite: boolean;
  canUndo?: boolean;
  isHidden?: boolean;
  myReaction?: string | null;
}

export interface StoriesResponse {
  stories: StoryListItem[];
  __offset: string;
  __deleted?: string[];
}

// Anonymous participation types
export interface AnonymousJoinRequest {
  anonymousId: string;
  displayName: string;
}

export interface AnonymousJoinResponse {
  story: Story;
  previousEntry?: Entry;
  participation: { order: number };
}

export interface AnonymousDetailResponse extends StoryDetailResponse {
  participation: { order: number };
}

export interface AnonymousAddEntryRequest {
  anonymousId: string;
  text: string;
}

// ============================================
// WebSocket Events
// ============================================

export interface WsStoryUpdated {
  storyId: string;
  currentTurn: number;
  participant: Participant;
}

export interface WsStoryCompleted {
  storyId: string;
  finalStory: string;
}

export interface WsYourTurn {
  storyId: string;
  previousEntry: Entry;
}

// ============================================
// Constants
// ============================================

export const STORY_GENRES: StoryGenre[] = ['comedy', 'horror', 'romance', 'scifi', 'fantasy', 'random'];

export const GENRE_LABELS: Record<StoryGenre, string> = {
  comedy: '😂 Comedy',
  horror: '👻 Horror',
  romance: '💕 Romance',
  scifi: '🚀 Sci-Fi',
  fantasy: '🔮 Fantasy',
  random: '🎲 Random',
};

export const GENRE_ICONS: Record<StoryGenre, string> = {
  comedy: '😂',
  horror: '👻',
  romance: '💕',
  scifi: '🚀',
  fantasy: '🔮',
  random: '🎲',
};

export const PARTICIPANT_COUNTS = [5, 7, 10] as const;

export const MAX_ENTRY_LENGTH = 280;

export const TURN_TIMEOUT_HOURS = 24;

// ============================================
// Story Completion Progress
// ============================================

export type CompletionStage =
  | 'moderating'
  | 'generating_story'
  | 'generating_image'
  | 'uploading'
  | 'done';

export const COMPLETION_STAGES: CompletionStage[] = [
  'moderating',
  'generating_story',
  'generating_image',
  'done',
];

export const COMPLETION_STAGE_LABELS: Record<CompletionStage, string> = {
  moderating: 'Checking content...',
  generating_story: 'Weaving your story...',
  generating_image: 'Creating cover art...',
  uploading: 'Saving...',
  done: 'Done!',
};

// ============================================
// Adventure Mode Types
// ============================================

export type AdventureTheme = 'fantasy' | 'detective' | 'horror' | 'romance' | 'scifi' | 'comedy' | 'intimate';

export type AdventureStatus = 'lobby' | 'active' | 'paused' | 'completed' | 'abandoned' | 'cancelled';

export type TurnMode = 'sequential' | 'free' | 'vote';

export type ImageStyle = 'manga' | 'comic' | 'realistic' | 'watercolor' | 'anime' | 'oil_painting' | 'sketch' | 'pixel' | 'digital_illustration';

export const IMAGE_STYLES: ImageStyle[] = [
  'manga',
  'comic',
  'realistic',
  'watercolor',
  'anime',
  'oil_painting',
  'sketch',
  'pixel',
  'digital_illustration',
];

export type CharacterMode = 'predefined' | 'custom' | 'ai_assigned';

export interface AdventureConfig {
  moderation: boolean;
  maxRounds: number;
  turnMode: TurnMode;
  imageStyle: ImageStyle;
  characterMode: CharacterMode;
}

export interface AdventureCharacter {
  name: string;
  description?: string;
  avatarUrl?: string;
}

// NPC defined by creator at adventure setup
export type NPCRole = 'npc' | 'villain' | 'ally' | 'mysterious';

export interface AdventureNPC {
  name: string;
  description?: string;         // Brief description (personality, motivation)
  role?: NPCRole;               // Role in the story
}

export interface AdventureParticipant {
  userId?: string;
  anonymousId?: string;
  displayName: string;
  character?: AdventureCharacter;
  isCreator: boolean;
  joinedAt: Date;
}

// Key NPC identified from setting at adventure start
export interface KeyNPC {
  name: string;
  role: 'npc' | 'villain' | 'ally' | 'creature';
  appearance: string;         // Detailed visual description
  portraitUrl?: string;       // Generated portrait URL
}

// Story outline generated at adventure start - guides the narrative arc
export interface StoryOutline {
  language: string;           // Detected language (ISO 639-1 code)
  mainConflict: string;       // The central problem/goal of the story
  incitingIncident: string;   // What kicks off the adventure (round 2-3)
  risingAction: string;       // Complications and obstacles (rounds 4-60%)
  midpointTwist: string;      // Major revelation or shift (around 50%)
  climaxSetup: string;        // Building to the final confrontation (75-85%)
  resolution: string;         // How the story should conclude
  // Player character visual descriptions for image consistency
  characterVisuals: Array<{
    name: string;
    appearance: string;       // Detailed visual description for image prompts
    portraitUrl?: string;     // Generated portrait URL (stored separately)
  }>;
  // Key NPCs identified from setting (love interests, antagonists, etc.)
  keyNPCs?: KeyNPC[];
}

// Character registry entry - tracks all characters (players + NPCs) for visual consistency
export type CharacterRole = 'player' | 'npc' | 'villain' | 'ally' | 'creature';

export interface CharacterRegistryEntry {
  name: string;
  role: CharacterRole;
  appearance: string;         // Detailed visual description
  portraitUrl?: string;       // Generated portrait URL
  firstSeenRound: number;     // Round when character first appeared
}

// ============================================
// Adventure Premise & Goals
// ============================================

export type PremiseStatus = 'none' | 'generating' | 'pending' | 'approved' | 'regenerating' | 'failed';

// Story premise - defines goals, conditions, and narrative boundaries
export interface AdventurePremise {
  synopsis: string;             // 2-3 sentences: what this adventure is about
  setting: string;              // Expanded setting description
  objectives: string[];         // 2-4 explicit goals players should achieve
  successCondition: string;     // What constitutes "winning"
  failureCondition?: string;    // What constitutes "losing" (optional)
  challenges: string[];         // Key obstacles/puzzles along the way
  possibleEndings: string[];    // 2-3 ways this could end
  boundaries: string;           // What's NOT possible in this story
  roles?: Record<string, string>; // Player character name -> their special role/secret
  // For detective theme: hidden solution info
  solution?: {
    culprit?: string;           // Who did it (NPC name)
    motive?: string;            // Why they did it
    clues: string[];            // Key clues that point to the culprit
  };
  // Dynamic length: AI's assessment of story complexity
  complexity?: StoryComplexity;
}

// Tracks progress toward adventure objectives
export interface AdventureGoalProgress {
  achieved: string[];           // Objectives completed
  failed: string[];             // Objectives failed/impossible
  active: string[];             // Still in progress
  notes: string;                // Brief status summary
}

// Goal update per scene (how objectives changed in this scene)
export interface SceneGoalUpdate {
  achieved: string[];           // Objectives completed in this scene
  failed: string[];             // Objectives that became impossible in this scene
  progressNote: string;         // Brief note about progress
}

// AI reasoning for scene generation (stored for debugging)
export interface SceneReasoning {
  plotDirection: string;        // Why this narrative direction was chosen
  goalProgress: string;         // How this scene affects objectives
  suggestionsRationale: string; // Why these 3 options were provided
  narrativeState?: NarrativeState; // Dynamic pacing assessment
}

// ============================================
// Dynamic Adventure Length Types
// ============================================

// Where is the story heading right now?
export type NarrativeTrajectory =
  | 'toward_success'    // Players on track to achieve objectives
  | 'toward_failure'    // Too many mistakes, heading to bad ending
  | 'stuck'             // Going in circles, no progress
  | 'critical_moment'   // Next choice will determine success/failure
  | 'toward_critical_moment' // Approaching a decisive moment
  | 'finale_ready';     // Story naturally reached conclusion point

// Can the story still be saved?
export type RecoveryPossibility =
  | 'easy'              // Minor setbacks, plenty of paths to success
  | 'possible'          // Difficult but achievable with right choices
  | 'unlikely'          // Would need exceptional choices to recover
  | 'impossible';       // Bad ending is now inevitable, play it out

// What should this scene accomplish?
export type SceneIntent =
  | 'advance_plot'      // Normal story progression
  | 'add_tension'       // Players stalling - introduce urgency
  | 'offer_redemption'  // Give chance to recover from mistakes
  | 'seal_fate'         // Confirm bad ending trajectory
  | 'climax'            // Build to final confrontation
  | 'resolution';       // Wrap up the story

// AI's assessment of narrative state (per scene)
export interface NarrativeState {
  trajectory: NarrativeTrajectory;
  recovery: RecoveryPossibility;
  successPathVisible: boolean;      // Can AI see a path to good ending?
  roundsToGoodEnding?: number;      // If success path exists, estimate
  roundsToBadEnding?: number;       // If heading to failure, estimate
  nextSceneIntent: SceneIntent;     // What should next scene do?
  reasoning: string;                // AI's explanation
}

// AI's assessment of story complexity (in premise)
export interface StoryComplexity {
  estimatedMinRounds: number;       // Minimum needed to tell this story well
  estimatedMaxRounds: number;       // Beyond this would feel stretched
  factors: {
    objectiveCount: number;
    npcComplexity: 'simple' | 'moderate' | 'complex';
    plotTwists: number;
    worldDepth: 'shallow' | 'medium' | 'deep';
  };
  reasoning: string;                // Why this estimate
}

export interface Adventure {
  id: string;
  code: string;
  creatorId: string;
  theme: AdventureTheme;
  setting?: string;
  npcs?: AdventureNPC[];          // Pre-defined NPCs for the adventure
  title?: string;
  storyOutline?: StoryOutline;  // Generated at start to guide plot
  characterRegistry?: CharacterRegistryEntry[];  // All characters (players + NPCs) for visual consistency
  language?: string;  // Detected from setting/characters, used for scene generation
  // Premise - goals and narrative structure (generated in lobby)
  premise?: AdventurePremise;
  premiseStatus: PremiseStatus;
  goalProgress?: AdventureGoalProgress;  // Updated each scene
  narrativeState?: NarrativeState;       // Current narrative trajectory (dynamic pacing)
  config: AdventureConfig;
  participants: AdventureParticipant[];
  maxParticipants: number;
  allowMidJoin: boolean;
  status: AdventureStatus;
  currentRound: number;
  currentTurnUserId?: string;
  aiProvider?: AIProvider;
  aiModel?: string;
  imageProvider?: ImageProvider;
  imageModel?: string;
  createdAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  // Comic preview fields (populated for completed adventures in list)
  comicCoverUrl?: string;
  comicCoverMediumUrl?: string;
  comicCoverThumbUrl?: string;
  comicSynopsis?: string;
}

// ============================================
// Adventure Scene Types
// ============================================

export type SceneType = 'intro' | 'normal' | 'twist' | 'climax' | 'pre_finale' | 'finale';

export interface SceneAction {
  userId?: string;
  anonymousId?: string;
  characterName: string;
  text: string;
  timestamp: Date;
}

export interface AdventureScene {
  id: string;
  adventureId: string;
  round: number;
  narrative: string;
  imageUrl?: string;
  imageMediumUrl?: string;
  imageThumbUrl?: string;
  imagePrompt?: string; // The prompt used for image generation
  imagePromptFiltered?: boolean; // True if safe prompt was used instead of original
  suggestions: string[];
  action?: SceneAction;
  sceneType: SceneType;
  isKeyMoment: boolean;
  // AI reasoning for this scene (stored for debugging/analysis)
  reasoning?: SceneReasoning;
  // Goal update tracking (stored for debugging/analysis)
  goalUpdate?: SceneGoalUpdate;
  createdAt: Date;
}

// ============================================
// Comic Types
// ============================================

export type ComicVisibility = 'private' | 'unlisted' | 'public';

export type PanelType = 'intro' | 'scene' | 'action' | 'twist' | 'climax' | 'finale';

export interface ComicAuthor {
  userId?: string;
  displayName: string;
  characterName: string;
}

export type ComicCharacterRole = 'player' | 'npc' | 'villain' | 'ally' | 'creature';

export interface ComicCharacter {
  name: string;
  role: ComicCharacterRole;
  appearance: string;
  portraitUrl?: string;
}

export interface ComicPanel {
  imageUrl: string;
  imageMediumUrl?: string;
  imageThumbUrl?: string;
  narrative: string;
  action?: {
    characterName: string;
    text: string;
  };
  panelType: PanelType;
}

/**
 * One entry in a comic's narrative-revision history.
 *
 * Revisions snapshot title + per-panel narratives only — never images,
 * which stay on the live Comic doc and are referenced by index. The
 * `isActive` marker on a revision means its content currently matches
 * the live Comic; useful so the UI can render a "current" badge without
 * needing a separate "active revision id" pointer on the Comic.
 */
export type ComicRevisionSource = 'initial' | 'pre_regenerate' | 'pre_restore';

export interface ComicRevisionListItem {
  id: string;
  revisionNumber: number;
  title: string;
  source: ComicRevisionSource;
  triggeredByUserId?: string;
  createdAt: Date;
  isActive: boolean;
}

export interface ComicRevisionDetail {
  id: string;
  revisionNumber: number;
  title: string;
  synopsis?: string;
  /** Index-aligned with the live Comic.panels[] — narrative text only. */
  panelNarratives: string[];
  source: ComicRevisionSource;
  createdAt: Date;
}

export interface Comic {
  id: string;
  adventureId: string;
  title: string;
  synopsis?: string;
  coverImageUrl: string;
  coverImageMediumUrl?: string;
  coverImageThumbUrl?: string;
  theme: string;
  panels: ComicPanel[];
  authors: ComicAuthor[];
  characters?: ComicCharacter[]; // Character gallery with portraits
  panelCount: number;
  visibility: ComicVisibility;
  shareCode: string;
  slug?: string;
  views: number;
  likes: number;
  likedBy?: string[];
  reactions?: Partial<Record<string, number>>;
  reactionCount?: number;
  language?: string;
  isApproved: boolean;
  isFeatured: boolean;
  reportCount: number;
  createdAt: Date;
}

// ============================================
// Adventure API Types
// ============================================

export interface CreateAdventureRequest {
  theme: AdventureTheme;
  setting?: string;
  npcs?: AdventureNPC[];          // Pre-defined NPCs for the adventure
  config?: Partial<AdventureConfig>;
  maxParticipants?: number;
  character?: AdventureCharacter;
}

export interface CreateAdventureResponse {
  adventure: Adventure;
  shareUrl: string;
}

export interface JoinAdventureRequest {
  character?: AdventureCharacter;
  displayName?: string;
}

export interface AdventureActionRequest {
  text?: string;
  suggestionIndex?: number;
}

export interface AdventureDetailResponse {
  adventure: Adventure;
  scenes: AdventureScene[];
  currentScene?: AdventureScene;
  isMyTurn: boolean;
  comicId?: string;
}

export interface ComicListItem {
  id: string;
  title: string;
  synopsis?: string;
  coverImageUrl: string;
  coverImageMediumUrl?: string;
  coverImageThumbUrl?: string;
  theme: string;
  authors: ComicAuthor[];
  panelCount: number;
  views: number;
  likes: number;
  reactions?: Partial<Record<string, number>>;
  reactionCount?: number;
  language?: string;
  shareCode: string;
  createdAt: Date;
}

export interface ComicDetailResponse {
  comic: Comic;
  isLiked: boolean;
  /**
   * True if the requesting user is an author (participant of the source
   * adventure). Server-derived — clients should use this rather than
   * comparing comic.authors[].userId, which historically had ObjectId vs
   * string serialization quirks across older data.
   */
  isAuthor?: boolean;
  myReaction?: ReactionEmoji | null;
}

// ============================================
// Adventure Constants
// ============================================

export const ADVENTURE_THEMES: AdventureTheme[] = [
  'fantasy',
  'detective',
  'horror',
  'romance',
  'scifi',
  'comedy',
];

export const ADVENTURE_THEME_LABELS: Record<AdventureTheme, string> = {
  fantasy: '⚔️ Fantasy',
  detective: '🔍 Detective',
  horror: '👻 Horror',
  romance: '💕 Romance',
  scifi: '🚀 Sci-Fi',
  comedy: '😂 Comedy',
  intimate: '🔥 Intimate',
};

export const ADVENTURE_THEME_ICONS: Record<AdventureTheme, string> = {
  fantasy: '⚔️',
  detective: '🔍',
  horror: '👻',
  romance: '💕',
  scifi: '🚀',
  comedy: '😂',
  intimate: '🔥',
};

export const IMAGE_STYLE_LABELS: Record<ImageStyle, string> = {
  manga: '🎌 Manga',
  comic: '💥 Comic',
  realistic: '📷 Realistic',
  watercolor: '🎨 Watercolor',
  anime: '✨ Anime',
  oil_painting: '🖼️ Oil Painting',
  sketch: '✏️ Sketch',
  pixel: '👾 Pixel Art',
  digital_illustration: '🖌️ Digital Art',
};

export const MAX_ACTION_LENGTH = 500;

export const DEFAULT_MAX_ROUNDS = 15;

// ============================================
// Behind the Scenes Types
// ============================================

export type BTSContentType = 'blooper' | 'deleted_scene' | 'behind_the_scenes' | 'interview';

export interface BTSItem {
  type: BTSContentType;
  title: string;
  description: string;
  imageUrl?: string;
  imageThumbUrl?: string;
  imageMediumUrl?: string;
  characterName?: string; // for interviews
}

export interface BehindTheScenes {
  id: string;
  comicId: string;
  items: BTSItem[];
  status: 'pending' | 'generating' | 'completed' | 'failed';
  error?: string;
  generatedAt?: Date;
  createdAt: Date;
}

export interface BTSGenerationProgress {
  stage: 'analyzing' | 'generating_content' | 'generating_images' | 'finalizing';
  current: number;
  total: number;
  message: string;
}

export const BTS_CONTENT_TYPE_LABELS: Record<BTSContentType, string> = {
  blooper: '🎭 Bloopers',
  deleted_scene: '✂️ Deleted Scenes',
  behind_the_scenes: '📸 Behind the Scenes',
  interview: '🎤 Cast Interviews',
};

export const BTS_GENERATION_STAGES = [
  'analyzing',
  'generating_content',
  'generating_images',
  'finalizing',
] as const;

// ============================================
// Reactions
// ============================================

export const REACTION_EMOJIS = ['😂', '❤️', '😮', '😢', '🔥'] as const;
export type ReactionEmoji = typeof REACTION_EMOJIS[number];

export type ReactionTargetType = 'story' | 'comic';

export type ReactionCounts = Partial<Record<ReactionEmoji, number>>;

export interface ReactionState {
  reactions: ReactionCounts;
  reactionCount: number;
  myReaction: ReactionEmoji | null;
}
